// src/components/ui/CaseACocher.jsx
// ============================================================================
// LA CASE À COCHER DE KLASYS (convention du 01/11/2026, DESIGN-SYSTEM.md)
// ============================================================================
// Copie fidèle de la case de « Exporter en PDF » des emplois du temps
// (ExportTimetableModal.jsx), retenue par Daniel comme LA case de KLASYS : la
// case native est cachée (sr-only, toujours lue par le clavier et les lecteurs
// d'écran), une case dessinée de 20 px la remplace :
//   - décochée : bord gris, bord bleu au survol ;
//   - cochée : aplat #246990, coche blanche (Check, 13 px) ;
//   - partielle (« Tout sélectionner » d'un groupe à moitié coché) : le même
//     aplat, un trait blanc (Minus, 13 px) ;
//   - jamais de dégradé ni d'agrandissement : une case qui grandit quand on la
//     coche fait bouger toute la ligne.
//
// Ajouts à la référence : un anneau quand la case reçoit le focus au clavier,
// l'état désactivé (case grisée, curseur interdit) pour une case que le droit
// de l'agent ne lui laisse pas changer, et l'état partiel.
//
// Deux pièces :
//   - CaseACocher : la ligne complète, cliquable, grisée au survol (libellé,
//     description, raison d'un refus). Seule, elle se pose dans un cadre
//     `overflow-hidden rounded-lg border border-gray-200` ; en liste, une
//     ligne par case, séparées par `border-b border-gray-100`.
//       <CaseACocher checked={x} onChange={(coche) => ...} label="Classe sans matière" />
//   - BoiteACocher : la case seule, pour une ligne qui porte davantage (un
//     coefficient, des badges) : la ligne est alors un <label> qui la contient.
//       <label className="flex items-center gap-3 ..."><BoiteACocher checked={x} onChange={...} />...</label>

import React, { useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';

export const BoiteACocher = ({
  checked = false,
  indetermine = false,
  onChange,
  disabled = false,
  className = '',
  ...autres
}) => {
  const entree = useRef(null);
  const coche = Boolean(checked);
  const partielle = !coche && Boolean(indetermine);
  // L'état partiel n'existe pas en attribut HTML : il se pose sur l'élément.
  useEffect(() => {
    if (entree.current) entree.current.indeterminate = partielle;
  }, [partielle]);

  let ton = 'border-gray-300 hover:border-[#246990]';
  if (coche || partielle) ton = disabled ? 'border-transparent bg-[#246990]/50' : 'border-transparent bg-[#246990]';
  else if (disabled) ton = 'border-gray-200 bg-gray-100';

  return (
    <span className={`relative flex flex-shrink-0 items-center ${className}`}>
      <input
        ref={entree}
        type="checkbox"
        checked={coche}
        onChange={(e) => onChange?.(e.target.checked)}
        disabled={disabled}
        className="peer sr-only"
        {...autres}
      />
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#246990]/40 ${ton}`}
      >
        {coche && <Check className="text-white" size={13} />}
        {partielle && <Minus className="text-white" size={13} />}
      </span>
    </span>
  );
};

const CaseACocher = ({
  checked = false,
  indetermine = false,
  onChange,
  disabled = false,
  label,
  description = null,
  aide = null,
  className = ''
}) => {
  const coche = Boolean(checked);
  const enColonne = Boolean(description || aide);

  return (
    <label
      className={`flex ${enColonne ? 'items-start' : 'items-center'} px-4 py-3 transition-colors ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
      } ${className}`}
    >
      <BoiteACocher
        checked={coche}
        indetermine={indetermine}
        onChange={onChange}
        disabled={disabled}
        className={enColonne ? 'mt-0.5' : ''}
      />
      <span className="ml-3 min-w-0">
        <span className={`block text-sm font-medium transition-colors ${coche ? 'text-gray-900' : 'text-gray-700'}`}>
          {label}
        </span>
        {description && <span className="mt-0.5 block text-xs text-gray-500">{description}</span>}
        {aide && <span className="mt-1 block text-xs text-amber-700">{aide}</span>}
      </span>
    </label>
  );
};

export default CaseACocher;
